import { Button, Col, Form, Input, message, Modal, Result, Row, Tabs } from "antd"
import { createContext, forwardRef, useCallback, useImperativeHandle, useRef, useState } from "react"
import { useForm, UseFormReturn } from "react-hook-form"
import * as yup from 'yup'
import { EMAIL_REGEX } from "@/utils/validates/validateEmail"
import { yupResolver } from "@hookform/resolvers/yup"
import { getFirstNameLastNameByFullName } from "@/utils/string/getFirstNameLastNameByFullName"
import { validatePhoneNumber } from "@/utils/validates/validatePhoneNumber"
import CharterFormInfomation from "./CharterFormInfomation"
import { useAppDispatch, useAppSelector } from "@/lib/redux/hooks"
import { thunks } from "@/lib/redux/thunks"
import CharterFormBook from "./CharterFormBook"
import { useTETranslation } from "@/hooks/app/useTETranslation"
import { useAirports } from "@/hooks/flight/useAirports"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCheckCircle } from "@fortawesome/free-solid-svg-icons"
import Text from "../Typography/Text"
import { useTranslation } from "react-i18next"
import { toast } from "react-toastify"
import ControllerWarpper from "../ControllerWarpper"
import ModalConfirmPayment, { ModalConfirmPaymentRef } from "./ModalConfirmPayment"

export type ModalCharterRef = {
  close: () => void,
  open: () => void
}

const schema = yup.object({
  passengers: yup.array(
    yup.object({
      gender: yup.string(),
      fullname: yup.string(),
    }).required().test("passengers.fullname", "booking.fullname_input_required_message", function (value) {
      if (!value.fullname) return false
      const { firstName, lastName } = getFirstNameLastNameByFullName(value.fullname || "")
      if (!firstName || !lastName) {
        return false
      }
      return true
    }),
  ).required(),
  flight: yup.string().required("flight.charter_choose_flight_required"),
  contact: yup.object({
    fullname: yup.string().test("contact.fullname", "booking.fullname_input_required_message", function (value) {
      if (!value) return false
      const { firstName, lastName } = getFirstNameLastNameByFullName(value || "")
      if (!firstName || !lastName) {
        return false
      }
      return true
    }),
    phone: yup.string().required("booking.phonenumber_input_required_message")
      .test(
        "contact.phone",
        "booking.phonenumber_input_invalid_message",
        validatePhoneNumber
      ),
    email: yup.string().trim().required("booking.email_input_required_message").matches(EMAIL_REGEX, "booking.email_input_rule_message"),
  }).required(),
  orderCode: yup.string().optional().nullable(),
  verifyCode: yup.string().optional().nullable(),
}).required()

export const ModalCharterContext = createContext<{
  form: UseFormReturn<any, any> | null,
  formBook: UseFormReturn<any, any> | null,
  formVerify: UseFormReturn<any, any> | null,
}>({
  form: null,
  formBook: null,
  formVerify: null
})

const ModalCharter = forwardRef<ModalCharterRef, any>((props, ref) => {
  const [open, setOpen] = useState<boolean>(false)
  const form = useForm({
    resolver: yupResolver(schema)
  })
  const formBook = useForm()
  const dispatch = useAppDispatch()
  const sendingRequest = useAppSelector(state => state.user.customer_temp.loading)
  const booking = useAppSelector(state => state.user.force_send_mail.loading)
  const [tab, setTab] = useState<"info" | "book" | 'success'>('info')
  const { getFormatCurrency } = useTETranslation()
  const { t } = useTranslation()
  const formVerify = useForm()
  const refModalConfirmPayment = useRef<ModalConfirmPaymentRef>(null)
  const [sentBooking, setSentBooking] = useState<boolean>(false)

  const handleClose = useCallback(() => {
    setTab('info')
    setOpen(false)
  }, [])

  const handleSubmitRequest = useCallback((values: any) => {
    dispatch(thunks.customer_temp({
      body: {
        TYPE: 'JSON',
        title: `VE MAY BAY TET2026 - ${values.contact.fullname} - ${values.flight} ${getFormatCurrency(900000 * values.passengers.length)}`,
        fullName: values.contact.fullname,
        phoneNumber: values.contact.phone,
        source: "web",
        note: ""
      }
    })).unwrap().then(() => {
      toast.success(t("booking.request_booking_succeed"))
      handleClose()
    }).catch(() => {

    })
  }, [])

  const handleBook = (values: any) => {
    if (sentBooking) {
      refModalConfirmPayment.current?.open()
      return;
    }
    const verifyCode = `TECT26${Date.now().toString().slice(-6)}`
    form.setValue('verifyCode', verifyCode)
    dispatch(thunks.force_send_mail({
      body: {
        TYPE: 'JSON',
        to: values?.contact?.email,
        bcc: "booking.th@hophilong.vn",
        subject: `MÃ XÁC NHẬN ĐƠN HÀNG - VE MAY BAY TET2026`,
        body: `<div><p><span>Mã xác nhận đơn hàng: </span><span style="font-weight:bold">${verifyCode}</span></p></div>`,
        type: "TE"
      }
    }))
      .unwrap()
      .then(() => {
        refModalConfirmPayment.current?.open()
        setSentBooking(true)
      })
      .catch(() => {

      }).finally(() => {
        // handleClose()
      })
  }

  const handleOpen = useCallback(() => {
    form.reset({
      passengers: [
        {
          fullname: "",
          gender: "Mr"
        }
      ],
      contact: {
        fullname: "",
        email: "",
        phone: ""
      },
      flight: ""
    })
    setSentBooking(false)
    setTab('info')
    setOpen(true)
  }, [])

  useImperativeHandle(ref, () => {
    return {
      close: handleClose,
      open: handleOpen
    }
  }, [])

  return (
    <ModalCharterContext.Provider
      value={{
        form,
        formBook,
        formVerify
      }}
    >
      <ModalConfirmPayment 
        ref={refModalConfirmPayment}
        onDone={() => {
          setTab('success')
        }}
      />
      <Modal
        open={open}
        title="VÉ MÁY BAY TẾT 2026"
        onCancel={handleClose}
        width={1000}
        centered
        mask={{ closable: false }}
        footer={(
          <Row gutter={15}>
            {tab === 'info' ? (
              <>
                {/* <Col flex={1}>
                  <Button
                    block
                    onClick={handleClose}
                    disabled={sendingRequest}
                  >
                    Hủy
                  </Button>
                </Col> */}
                <Col flex={1}>
                  <Button
                    size='large'
                    block
                    type='primary'
                    ghost
                    onClick={form.handleSubmit(handleSubmitRequest, (errror) => {
                      console.error("form validate errors", errror)
                    })}
                    loading={sendingRequest}
                  >
                    {t("booking.submit_request")}
                  </Button>
                </Col>
                <Col flex={1}>
                  <Button
                    block
                    size='large'
                    type='primary'
                    disabled={sendingRequest}
                    onClick={form.handleSubmit(() => {
                      setTab("book")
                      form.setValue("orderCode", `TEC${+new Date()}`)
                    })}
                  >
                    {t("flight.book")}
                  </Button>
                </Col>
              </>
            ) : tab === 'book' ? (
              <>
                <Col flex={1}>
                  <Button
                    block
                    size='large'
                    onClick={() => {
                      setTab('info')
                      setSentBooking(false)
                    }}
                    disabled={booking}
                  >
                    {t("common.back")}
                  </Button>
                </Col>
                <Col flex={1}>
                  <Button
                    type='primary'
                    block
                    size='large'
                    onClick={form.handleSubmit(handleBook)}
                    loading={booking}
                  >
                    {sentBooking ? t(`common.confirm_paid_payment`) : t("common.confirm")}
                  </Button>
                </Col>
              </>
            ) : tab === 'success' ? (
              <>
                <Col flex={1}>
                  <Button
                    type='primary'
                    block
                    size='large'
                    onClick={handleClose}
                  >
                    {t("common.done")}
                  </Button>
                </Col>
              </>
            ) : null}
          </Row>
        )}
      >
        <Form
          layout='vertical'
          style={{ minHeight: 400 }}
        >
          <Tabs
            renderTabBar={() => <></>}
            activeKey={tab}
            items={[
              {
                label: "info",
                key: "info",
                tabKey: "info",
                children: <CharterFormInfomation />
              },
              {
                label: "book",
                key: "book",
                tabKey: "book",
                children: <CharterFormBook />
              },
              {
                label: "success",
                key: "success",
                tabKey: "success",
                children: (
                  <Result
                    icon={<Text type='success'><FontAwesomeIcon size='5x' icon={faCheckCircle} /></Text>}
                    status='success'
                    title={t("flight.charter_status_success_title")}
                    subTitle={t("flight.charter_status_success_sub_title", { phone: process.env.NEXT_PUBLIC_COMPANY_PHONE })}
                  />
                )
              }
            ]}
          />
        </Form>
      </Modal>
    </ModalCharterContext.Provider>
  )
})

export default ModalCharter